import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillHourProfile } from '../../../types';
import { DrillTile, type DrillTileSpan } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';

/** Hour totals over the picked weekdays (all when none is picked), on one
 *  hour axis for the whole week so a narrowed profile keeps its place. */
export function hourProfileBars(
  profile: DrillHourProfile,
  selectedDows: number[] = [],
): { hour: number; value: number }[] {
  const hours = profile.cells.map(c => c.hour);
  if (!hours.length) return [];
  const lo = Math.min(...hours);
  const hi = Math.max(...hours);
  const keep = selectedDows.length ? new Set(selectedDows) : null;
  const byHour = new Map<number, number>();
  for (const c of profile.cells) {
    if (keep && !keep.has(c.dow)) continue;
    byHour.set(c.hour, (byHour.get(c.hour) ?? 0) + c.value);
  }
  const out: { hour: number; value: number }[] = [];
  for (let h = lo; h <= hi; h += 1) out.push({ hour: h, value: byHour.get(h) ?? 0 });
  return out;
}

export function DrillHourProfileSection({
  profile,
  selectedDows = [],
  dayLabels,
  onClear,
  span = 4,
}: {
  profile: DrillHourProfile;
  selectedDows?: number[];
  /** ISO weekday → label, from the grid's rows. */
  dayLabels?: Map<number, string>;
  onClear?: () => void;
  span?: DrillTileSpan;
}) {
  const bars = useMemo(
    () => hourProfileBars(profile, selectedDows),
    [profile, selectedDows],
  );
  const total = bars.reduce((a, b) => a + b.value, 0);
  const chartData = bars.map(b => ({
    name: String(b.hour).padStart(2, '0'),
    fullName: fmt.clock(b.hour * 60),
    value: b.value,
  }));
  const days = [...selectedDows]
    .sort((a, b) => a - b)
    .map(d => dayLabels?.get(d) ?? String(d))
    .join(', ');
  const nKey =
    profile.unit === 'reservations'
      ? 'performance:drillUi.nReservations'
      : 'performance:drillUi.nGuests';

  return (
    <DrillTile
      sectionId="hour-profile"
      span={span}
      title={i18n.t(`performance:drillUi.hourTitle_${profile.measure}`)}
      meta={
        selectedDows.length
          ? i18n.t('performance:drillUi.hourSomeDays', { days })
          : i18n.t('performance:drillUi.hourAllDays')
      }
      n={i18n.t(nKey, { count: total, n: fmt.num(total) })}
    >
      {selectedDows.length && onClear ? (
        <button type="button" className="ddrill-hour-clear" onClick={onClear}>
          {i18n.t('performance:drillUi.hourClear')}
        </button>
      ) : null}
      <DrillBarChart
        data={chartData}
        fmtVal={v => fmt.num(v)}
        height={168}
        fill={span !== 12}
        maxHeight={320}
      />
    </DrillTile>
  );
}
